import { makeAutoObservable, runInAction } from 'mobx';

import { VideoTooLargeError } from '@/lib/voltrVideo';
import { voltrService } from '@/services/voltr';
import { dealsStore } from '@/stores/deals.store';
import { resumeOnReturn } from '@/stores/uploadResume';
import { messageFor } from '@/services/errors';
import { sendVideo } from '@/stores/voltrUploadSend';
import { Abandoned, freshItem as fresh, inProgress, isTerminalFailure, withoutPick, withPicks, type PickedVideo, type UploadContext, type UploadItem } from '@/stores/voltrUploadTypes';

export type { PickedVideo, UploadContext, UploadItem, UploadPhase } from '@/stores/voltrUploadTypes';

const UPLOAD_POLL_MS = 3_000;
const UPLOAD_POLL_TRIES = 40;
const FAILED = "The upload didn't go through. Try again.";
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
/**
 * MarketPryce Social (Voltr): the videos picked for one deal (and, when
 * replacing, one sent-back video), uploaded one after another straight from
 * the phone to Voltr's storage; the server creates each job and reports when
 * it's ready. One failing doesn't stop the rest. Every Action the server
 * returns goes to dealsStore so the deal page follows along.
 */
class VoltrUploadStore {
  items: UploadItem[] = [];
  context: UploadContext | null = null;
  /** A loop is working. Only that loop clears it, so a logout can't start a second one while it unwinds. */
  running = false;
  /** The Action that loop is uploading for, so a Cancel's unwinding loop never reads as another deal's upload. */
  runningFor: number | null = null;
  /** Bumped by reset(): work started under an older value belongs to the previous user. */
  private epoch = 0;
  private run = 0;
  private abort: AbortController | null = null;
  private resumer = resumeOnReturn(() => this.running, () => void this.uploadAll());

  constructor() { makeAutoObservable<this, 'resumer'>(this, { resumer: false }); }

  /** Changes on logout or Cancel; a picker that was open across it must drop its result. */
  get generation(): number { return this.epoch; }

  /** Every picked video is with Voltr (ready, or still processing there). */
  get settled(): boolean {
    return this.items.length > 0 && this.items.every((i) => i.phase === 'done' || i.phase === 'waiting');
  }

  /** The picks are for this deal's Action (whatever video they replace). */
  isFor(actionId: number): boolean { return this.context?.actionId === actionId; }

  /** Another deal's upload is still going, so this one waits. */
  busyElsewhere(actionId: number): boolean { return this.running && this.runningFor !== actionId; }

  /** Picks for this deal not yet with Voltr, or an upload of them running: a new pick would replace them. */
  hasPending(actionId: number): boolean {
    return (this.running && this.runningFor === actionId) || (this.isFor(actionId) && this.items.some((i) => i.phase !== 'done'));
  }

  /** The picks and what they're for, replacing any earlier ones; ignored while an upload runs. */
  setVideos(videos: PickedVideo[], context: UploadContext): void {
    if (!this.running) [this.context, this.items] = [context, videos.map(fresh)];
  }

  /** A pick joins this deal's waiting ones ("Add another video") under the rights shown now; a replacement, or another deal's, starts over. False when an upload started meanwhile. */
  addVideos(videos: PickedVideo[], context: UploadContext): boolean {
    if (this.running) return false;
    const adding = !context.replaces && !this.context?.replaces && this.isFor(context.actionId);
    this.items = adding ? withPicks(this.items, videos) : videos.map(fresh);
    this.context = context;
    return true;
  }

  /** The ✕ on a card: drops that pick (withoutPick); ignored while an upload runs. */
  remove(key: string): void {
    if (this.running) return;
    this.items = withoutPick(this.items, key);
    if (this.items.length === 0) this.context = null;
  }

  /** Forgets the videos Voltr has ready (they're on the step now); a running upload is left alone. */
  dropDone(): void {
    if (this.running) return;
    this.items = this.items.filter((i) => i.phase !== 'done');
    if (this.items.length === 0) this.context = null;
  }

  /** Uploads every picked video not yet with Voltr, in order. True when all of them are ready. */
  async uploadAll(): Promise<boolean> {
    const context = this.context;
    if (!context) return false;
    const ok = await this.work((epoch, signal) => async (item) => {
      if (item.phase === 'done' || item.phase === 'waiting') return;
      // Every byte already reached Voltr and only a status check failed: ask again, never send it twice.
      if (item.jobId) return this.awaitReady(item, context.actionId, epoch);
      await this.send(item, context, epoch, signal);
    });
    return ok && this.items.every((i) => i.phase === 'done');
  }

  /** After a `waiting` upload: ask Voltr again whether it finished. Nothing is re-read or re-sent. */
  async checkWaiting(): Promise<void> {
    const context = this.context;
    if (!context) return;
    await this.work((epoch) => async (item) => {
      if (item.phase === 'waiting' && item.jobId) await this.awaitReady(item, context.actionId, epoch);
    });
  }

  /** Runs `step` on each item in turn; one failing lands on that item and the rest carry on. False when abandoned. */
  private async work(step: (epoch: number, signal: AbortSignal) => (item: UploadItem) => Promise<void>): Promise<boolean> {
    if (this.running) return false;
    const epoch = this.epoch;
    const run = (this.run += 1);
    const abort = new AbortController();
    this.running = true;
    this.runningFor = this.context?.actionId ?? null;
    this.abort = abort;
    const each = step(epoch, abort.signal);
    try {
      for (const item of [...this.items]) {
        if (this.epoch !== epoch) return false;
        try {
          await each(item);
        } catch (err) {
          if (err instanceof Abandoned || this.epoch !== epoch) return false;
          runInAction(() => Object.assign(item, { phase: 'error', error: err instanceof VideoTooLargeError ? err.message : messageFor(err, FAILED) }));
        }
      }
      return this.epoch === epoch;
    } finally {
      runInAction(() => {
        if (this.run === run) {
          this.running = false;
          this.runningFor = null;
          this.abort = null;
          this.resumer.runEnded();
        }
      });
    }
  }

  /** Sends the video's bytes (see sendVideo), then polls until Voltr reports `ready`. */
  private async send(item: UploadItem, context: UploadContext, epoch: number, signal: AbortSignal): Promise<void> {
    const jobId = await sendVideo(item, context, () => this.assertLive(epoch), (sent) => {
      if (this.epoch === epoch) item.sentBytes = sent;
    }, signal);
    this.assertLive(epoch);
    runInAction(() => void (item.jobId = jobId));
    await this.awaitReady(item, context.actionId, epoch);
  }

  /** Polls the video's job: `done` when Voltr is ready, `waiting` when it's still working after the last try. */
  private async awaitReady(item: UploadItem, actionId: number, epoch: number): Promise<void> {
    const jobId = item.jobId;
    if (!jobId) return;
    runInAction(() => Object.assign(item, { phase: 'processing', error: null }));
    for (let i = 0; i < UPLOAD_POLL_TRIES; i += 1) {
      this.assertLive(epoch);
      const { action, upload } = await voltrService.uploadStatus(actionId, jobId);
      this.assertLive(epoch);
      dealsStore.applyAction(action);
      if (upload.status === 'ready') return runInAction(() => void (item.phase = 'done'));
      if (isTerminalFailure(upload)) {
        // Voltr gave up on this job: a retry sends the video again.
        runInAction(() => void (item.jobId = null));
        throw new Error(upload.error_code ?? upload.status);
      }
      await sleep(UPLOAD_POLL_MS);
    }
    this.assertLive(epoch);
    runInAction(() => void (item.phase = 'waiting'));
  }

  /** Throws Abandoned once a logout (reset) disowned the work started under `epoch`. */
  private assertLive(epoch: number): void {
    if (this.epoch !== epoch) throw new Abandoned();
  }

  /**
   * The athlete's Cancel on the uploading card: stops the bytes in flight and
   * drops that video; the other picks stay, ready to submit. Voltr's
   * half-made job expires on its own, and the deal page's sync clears it.
   */
  cancel(): void {
    this.epoch += 1;
    this.abort?.abort();
    this.items = this.items.filter((i) => !inProgress(i));
    if (this.items.length === 0) this.context = null;
  }

  /** Logout: forget everything, stop the bytes in flight, and disown the running loop (it clears `running` as it exits). */
  reset(): void {
    this.epoch += 1;
    this.abort?.abort();
    this.items = [];
    this.context = null;
  }
}

export const voltrUploadStore = new VoltrUploadStore();
